import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Trash2, Instagram, CheckCircle2, Bot, User, Sun, RefreshCw, FileText, CalendarClock, AlertTriangle } from "lucide-react";

const SUGGESTIONS = [
  "Prépare et programme 7 posts pour la semaine prochaine",
  "Crée 3 idées de posts pour cette semaine avec légendes",
  "Prépare un post pour vendredi 18h et programme-le",
  "Génère un visuel et rédige une légende pour un nouveau produit",
];

const AUTONOMY_LABEL = {
  manuel: "Manuel · brouillons uniquement",
  assisté: "Assisté · valide avant publication",
  autonome: "Autonome · publie seul",
};

function ActionChips({ actions }) {
  if (!actions?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="assistant-actions">
      {actions.map((a, i) => (
        <span
          key={`${a.tool}-${i}`}
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/30 rounded-sm px-2 py-0.5"
        >
          <CheckCircle2 className="h-3 w-3" />
          {a.label}
        </span>
      ))}
    </div>
  );
}

export default function Assistant() {
  const { activeId, activeClient } = useClient();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    if (!activeId) return;
    try {
      const { data } = await api.get("/assistant/messages", { params: { client_id: activeId } });
      setMessages(data);
    } catch (err) {
      console.error("Failed to load assistant history:", err);
    }
  };

  const loadBriefing = async (refresh = false) => {
    if (!activeId) return;
    setBriefingLoading(true);
    try {
      const { data } = await api.get("/assistant/briefing", { params: { client_id: activeId, refresh } });
      setBriefing(data);
    } catch (err) {
      console.error("Failed to load briefing:", err);
    }
    setBriefingLoading(false);
  };

  useEffect(() => {
    setMessages([]);
    setBriefing(null);
    load();
    loadBriefing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    if (!activeId) return toast.error("Sélectionne d'abord une marque");
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, role: "user", content: msg, actions: [] }]);
    try {
      const { data } = await api.post("/assistant/chat", { client_id: activeId, message: msg });
      setMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, role: "assistant", content: data.reply, actions: data.actions, pending: data.pending },
      ]);
      if (data.changed) loadBriefing(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setMessages((prev) => [
        ...prev,
        { id: `err-${Date.now()}`, role: "assistant", content: "Désolée, une erreur est survenue. Réessaie.", actions: [] },
      ]);
    }
    setSending(false);
  };

  const clearChat = async () => {
    if (!window.confirm("Effacer toute la conversation avec NATYE-IA ?")) return;
    try {
      await api.delete("/assistant/messages", { params: { client_id: activeId } });
      setMessages([]);
      toast.success("Conversation effacée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const confirmPublish = async (post_id) => {
    setConfirming(post_id);
    try {
      await api.post("/assistant/confirm-publish", { client_id: activeId, post_id });
      toast.success("Publié sur Instagram ✅");
      setMessages((prev) =>
        prev.map((m) =>
          m.pending ? { ...m, pending: m.pending.filter((p) => p.post_id !== post_id) } : m
        )
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setConfirming(null);
  };

  return (
    <>
      <PageHeader overline={`Assistant · ${activeClient?.name || "—"}`} title="NATYE-IA">
        <div className="flex items-center gap-3">
          <span
            className="hidden md:inline text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground border border-white/15 rounded-sm px-2.5 py-1"
            data-testid="assistant-autonomy-badge"
          >
            {AUTONOMY_LABEL[activeClient?.ai_autonomy] || "Assisté"}
          </span>
          <button
            data-testid="assistant-clear-button"
            onClick={clearChat}
            disabled={!messages.length}
            className="flex items-center gap-1.5 border border-white/15 rounded-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-accent hover:border-accent/40 transition-colors disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" /> Effacer
          </button>
        </div>
      </PageHeader>

      <div className="flex flex-col h-[calc(100vh-8.5rem)]">
        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 lg:p-8" data-testid="assistant-messages">
          {/* Briefing du matin */}
          {(briefing || briefingLoading) && (
            <div
              className="max-w-3xl mx-auto mb-6 border border-primary/30 bg-primary/[0.06] rounded-sm p-4"
              data-testid="assistant-briefing"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-primary">
                  <Sun className="h-3.5 w-3.5" /> Le point du matin
                </span>
                <button
                  data-testid="assistant-briefing-refresh"
                  onClick={() => loadBriefing(true)}
                  disabled={briefingLoading}
                  className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                  title="Rafraîchir le briefing"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${briefingLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
              {briefingLoading && !briefing ? (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> NATYE-IA prépare ton point du jour…
                </p>
              ) : (
                <>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{briefing?.briefing}</p>
                  {briefing?.stats && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="inline-flex items-center gap-1.5 text-[11px] border border-white/12 rounded-sm px-2.5 py-1 text-muted-foreground">
                        <CalendarClock className="h-3.5 w-3.5 text-primary" /> {briefing.stats.scheduled} programmé(s)
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[11px] border border-white/12 rounded-sm px-2.5 py-1 text-muted-foreground">
                        <FileText className="h-3.5 w-3.5" /> {briefing.stats.drafts} brouillon(s) à valider
                      </span>
                      {briefing.stats.errors > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-[11px] border border-accent/40 rounded-sm px-2.5 py-1 text-accent">
                          <AlertTriangle className="h-3.5 w-3.5" /> {briefing.stats.errors} en erreur
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {messages.length === 0 && !sending ? (
            <div className="max-w-2xl mx-auto text-center pt-10">
              <div className="h-14 w-14 rounded-sm bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-5">
                <Sparkles className="h-7 w-7 text-primary" />
              </div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">
                Ton Community Manager IA
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                Donne tes consignes en langage naturel. NATYE-IA crée, planifie, modifie et supprime
                les posts de <b className="text-foreground">{activeClient?.name || "ta marque"}</b> à ta place —
                selon le niveau d'autonomie que tu as défini.
              </p>
              <div className="grid sm:grid-cols-2 gap-2.5 text-left">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`assistant-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="border border-white/12 rounded-sm px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:border-white/30 hover:bg-white/[0.02] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-5">
              {messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`assistant-msg-${m.role}`}
                  className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div
                    className={`h-8 w-8 rounded-sm flex items-center justify-center shrink-0 ${
                      m.role === "user" ? "bg-accent/20 text-accent" : "bg-primary/20 text-primary"
                    }`}
                  >
                    {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`max-w-[80%] ${m.role === "user" ? "items-end" : ""}`}>
                    <div
                      className={`rounded-sm px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-accent/15 border border-accent/25"
                          : "bg-secondary border border-white/10"
                      }`}
                    >
                      {m.content}
                    </div>
                    {m.role === "assistant" && <ActionChips actions={m.actions} />}
                    {m.pending?.length > 0 && (
                      <div className="mt-2 space-y-2" data-testid="assistant-pending">
                        {m.pending.map((p) => (
                          <div
                            key={p.post_id}
                            className="flex items-center gap-3 border border-accent/40 bg-accent/10 rounded-sm px-3 py-2.5"
                          >
                            <Instagram className="h-4 w-4 text-accent shrink-0" />
                            <span className="text-xs text-muted-foreground flex-1 truncate">
                              Publication en attente : « {p.caption}… »
                            </span>
                            <button
                              data-testid={`assistant-confirm-publish-${p.post_id}`}
                              onClick={() => confirmPublish(p.post_id)}
                              disabled={confirming === p.post_id}
                              className="flex items-center gap-1.5 bg-accent text-white rounded-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-accent/80 transition-colors disabled:opacity-50"
                            >
                              {confirming === p.post_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              )}
                              Confirmer
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex gap-3" data-testid="assistant-thinking">
                  <div className="h-8 w-8 rounded-sm bg-primary/20 text-primary flex items-center justify-center shrink-0">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="bg-secondary border border-white/10 rounded-sm px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    NATYE-IA travaille…
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* input */}
        <div className="border-t border-white/10 p-4 lg:px-8 bg-background">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="max-w-3xl mx-auto flex items-end gap-3"
          >
            <textarea
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder={`Donne une consigne à NATYE-IA pour ${activeClient?.name || "ta marque"}…`}
              disabled={sending || !activeId}
              className="flex-1 bg-secondary border border-white/15 rounded-sm px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors resize-none max-h-40 disabled:opacity-50"
            />
            <button
              type="submit"
              data-testid="assistant-send-button"
              disabled={sending || !input.trim() || !activeId}
              className="h-[46px] w-[46px] shrink-0 bg-primary text-primary-foreground rounded-sm flex items-center justify-center hover:bg-primary/80 transition-colors disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          </form>
          <p className="max-w-3xl mx-auto text-[10px] text-muted-foreground mt-2 text-center">
            NATYE-IA agit uniquement sur la marque active et n'accède jamais à tes données privées (comptes, tokens, RGPD).
          </p>
        </div>
      </div>
    </>
  );
}
